import { Box, BoxProps, styled } from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';
import { EColors, getColor } from 'theme';
import Toolbar, { ToolbarInsertOption } from './Toolbar';
import { DEFAULT_MAX_CHAR_LIMIT } from './utils';

export const Container = styled(Box, {
    shouldForwardProp: prop => prop !== 'error',
})<{ error: boolean }>(({ theme: { spacing }, error }) => ({
    height: 'auto',
    border: `1px solid ${error ? getColor(EColors.red) : getColor(EColors.bizlyOSBorder)}`,
    borderRadius: '12px',
    boxSizing: 'border-box',
    padding: spacing(1.25, 1.5),
    '&:focus-within': {
        borderColor: getColor(EColors.v3Dark),
    },
}));

/**
 * use this comeponent to render the content of the tiptap editor
 * Note: style of the this component is corresponded to the tiptap-input p classes
 * @param {BoxProps} props
 */
export const TiptapContentRender = styled(Box)<BoxProps>({
    fontSize: '1rem',
    lineHeight: '21px',
    '& > p': { margin: 0, paddingBottom: '1rem' },
    '& > ul': { margin: 0 },
    '& > ol': { margin: 0 },
});

type TiptapProps = {
    description: string;
    editable: boolean;
    onChange: (_: string) => void;
    onBlur: (_: string) => void;
    limit?: number;
    error?: boolean;
    insertOptions?: ToolbarInsertOption[];
};

export function Tiptap({
    description,
    editable,
    limit = DEFAULT_MAX_CHAR_LIMIT,
    error = false,
    onChange,
    onBlur: _onBlur,
    insertOptions,
}: TiptapProps) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Disable link if it's in the starter kit
                link: {
                    openOnClick: false,
                    autolink: true,
                    defaultProtocol: 'https',
                    protocols: ['http', 'https'],
                },
            }),
        ],
        content: description,
        editorProps: {
            attributes: {
                class: 'tiptap-input',
            },
        },
        onUpdate({ editor }) {
            const text = editor.getText();
            // Rollback changes if limit exceeded
            if (text.length > limit) {
                const truncated = text.slice(0, limit);
                editor.commands.setContent(truncated); // Reset content
            } else {
                const html = editor.getHTML();
                // Return empty string if content is just empty paragraph tags or whitespace
                onChange(text.trim() === '' ? '' : html);
            }
        },
        onBlur({ editor }) {
            const text = editor.getText();
            const html = editor.getHTML();
            // Return empty string if content is just empty paragraph tags or whitespace
            _onBlur(text.trim() === '' ? '' : html);
        },
        editable,
    });

    // `content` only seeds the editor once, at creation — it's otherwise uncontrolled. A parent
    // that hydrates its value asynchronously (e.g. the store syncing after the parcel fetch
    // resolves, which lands in an effect after this component has already mounted with the
    // pre-hydration value) would then show stale/empty content forever. Re-sync here, but only
    // when the incoming value actually differs from what the editor already holds, so typing
    // (which round-trips through onUpdate -> onChange -> this same prop) doesn't fight the caret.
    useEffect(() => {
        if (!editor) return;

        const isEditorEmpty = editor.getText().trim() === '';
        const isDescriptionEmpty = description.trim() === '';
        if (isEditorEmpty && isDescriptionEmpty) return;
        if (editor.getHTML() === description) return;

        editor.commands.setContent(description, { emitUpdate: false });
    }, [description, editor]);

    return (
        <Container error={error}>
            <Toolbar editor={editor} insertOptions={insertOptions} />
            <EditorContent editor={editor} />
        </Container>
    );
}
